import { useLayoutEffect, useState } from 'react';
import { useTableScrollBudget } from '@/contexts/TableScrollBudgetContext';
import { useKeepAlivePaneActive } from '@/layouts/keepAlivePaneContext';
import { useTableScrollYLayoutEligible } from '@/layouts/tableScrollLayout';

/**
 * 列表页 Table 动态 **`scroll.y`**，与 **`data-table-scroll-layout="fixed"`** 时 MainLayout Card 锁高链配合。
 *
 * **`options.enabled`**（通常 **`useEnableTableScrollYLayout() || isFullscreen`**）为 **false** 时：
 * 视口偏矮 **或** 路由未进白名单 **或** 实测表体预算不足 **或** 页面关闭固定表体；此时应**不设** **`scroll.y`**（返回 **`undefined`**），纵向由 **`main.main-container--natural-scroll`** 滚动。
 *
 * 视口 + 白名单满足但表体预算不足时，hook 经 Context 上报 **`budgetAllowsFixedLayout: false`**，布局壳退回 natural-scroll。
 */

/** 表体 scroll.y 全局下限（px）；页面一般不传 minTableBodyScrollPx，沿用此值；低于此值不写 scroll.y 并触发 natural-scroll */
export const TABLE_BODY_SCROLL_MIN = 300;
const PAGINATION_HEIGHT_FLOOR = 44;

export const MAIN_TOOLBAR_DEDUCT_SELECTORS = ['.main-toolbar'];
export const TABLE_SCROLL_Y_REMEASURE_EVENT = 'boss:table-scroll-y-remeasure';

export function dispatchTableScrollYRemeasure() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(TABLE_SCROLL_Y_REMEASURE_EVENT));
}

/**
 * @param {React.RefObject<HTMLElement | null>} tableRegionRef
 * @param {object} [options]
 * @param {boolean} [options.enabled]
 * @param {string} [options.heightRootClassName]
 * @param {number} [options.scrollYOffset]
 * @param {readonly string[]} [options.deductSiblingSelectors]
 * @param {number} [options.minTableBodyScrollPx]
 */
export function useTableScrollY(tableRegionRef, options = {}) {
    const layoutEligible = useTableScrollYLayoutEligible();
    const keepAliveActive = useKeepAlivePaneActive();
    const { reportTableScrollBudget } = useTableScrollBudget();
    const enabled = options.enabled ?? true;
    const shouldMeasure = layoutEligible || enabled;
    const { heightRootClassName, scrollYOffset = 0, minTableBodyScrollPx = TABLE_BODY_SCROLL_MIN } = options;
    const deductSiblingSelectorsKey = options.deductSiblingSelectors?.join('\0') ?? '';

    const [scrollY, setScrollY] = useState(undefined);

    useLayoutEffect(() => {
        if (!keepAliveActive) {
            return;
        }
        if (!shouldMeasure) {
            setScrollY(undefined);
            return;
        }

        const region = tableRegionRef.current;
        if (!region) return;

        const deductSelectors = deductSiblingSelectorsKey.length > 0 ? deductSiblingSelectorsKey.split('\0') : [];

        /** @type {ResizeObserver[]} */
        const resizeObservers = [];

        function buildClassSelector(className) {
            const trimmed = className.trim().replace(/^\./, '');
            const parts = trimmed.split(/\s+/).filter(Boolean);
            if (parts.length === 0) return '';
            return parts.map(part => `.${CSS.escape(part)}`).join('');
        }

        function resolveScrollBudget(heightRoot) {
            const mainFullscreen = region.closest('main.main-container.is-page-fullscreen');
            if (mainFullscreen) {
                const mainRect = mainFullscreen.getBoundingClientRect();
                const rr = region.getBoundingClientRect();
                const cs = getComputedStyle(region);
                const pb = Number.parseFloat(cs.paddingBottom) || 0;
                return Math.max(0, Math.floor(mainRect.bottom - rr.top - pb));
            }

            const useCardLayout =
                !!heightRoot
                && (() => {
                    const selector = buildClassSelector(heightRoot);
                    return selector !== '' ? !!region.closest(selector) : false;
                })();

            if (useCardLayout) {
                const rr = region.getBoundingClientRect();
                const cs = getComputedStyle(region);
                const pt = Number.parseFloat(cs.paddingTop) || 0;
                const pb = Number.parseFloat(cs.paddingBottom) || 0;
                return Math.max(0, Math.floor(rr.height - pt - pb));
            }

            const wrapper = region.querySelector('.ant-table-wrapper');
            return wrapper?.clientHeight ?? region.clientHeight;
        }

        /** 自然滚动下 region 会随表格撑高；用 main 底到 region 顶估算 fixed 布局时的可用高度 */
        function resolveFixedLayoutProbeBudget() {
            const mainFullscreen = region.closest('main.main-container.is-page-fullscreen');
            const main = mainFullscreen ?? region.closest('main.main-container');
            if (!main) return null;

            const mainRect = main.getBoundingClientRect();
            const rr = region.getBoundingClientRect();
            const cs = getComputedStyle(region);
            const pb = Number.parseFloat(cs.paddingBottom) || 0;
            return Math.max(0, Math.floor(mainRect.bottom - rr.top - pb));
        }

        function resolveBudgetForSufficiencyCheck(heightRoot) {
            const main = region.closest('main.main-container');
            const inNaturalLayout = main?.classList.contains('main-container--natural-scroll') && !main?.classList.contains('is-page-fullscreen');
            if (inNaturalLayout) {
                return resolveFixedLayoutProbeBudget();
            }
            return resolveScrollBudget(heightRoot);
        }

        function computeTableBodyNext(budgetRegionContentPx) {
            let budget = budgetRegionContentPx;
            if (heightRootClassName) {
                for (const sel of deductSelectors) {
                    for (const node of region.querySelectorAll(sel)) {
                        budget -= node.offsetHeight;
                    }
                }
            }
            const header = region.querySelector('.ant-table-header');
            const pagination = region.querySelector('.ant-table-pagination');
            const headerH = header?.offsetHeight ?? 0;
            const paginationMeasured = pagination
                ? (() => {
                      const h = pagination.offsetHeight;
                      const pcs = getComputedStyle(pagination);
                      return h + (Number.parseFloat(pcs.marginTop) || 0) + (Number.parseFloat(pcs.marginBottom) || 0);
                  })()
                : 0;
            const paginationH = paginationMeasured > 0 ? paginationMeasured : PAGINATION_HEIGHT_FLOOR;
            return Math.floor(budget - headerH - paginationH - scrollYOffset - 1);
        }

        function measure() {
            const budgetRegionContentPx = resolveScrollBudget(heightRootClassName);
            const next = computeTableBodyNext(budgetRegionContentPx);

            const probeBudgetPx = resolveBudgetForSufficiencyCheck(heightRootClassName);
            const probeNext = probeBudgetPx != null ? computeTableBodyNext(probeBudgetPx) : next;
            const budgetAllowsFixedLayout = probeNext >= minTableBodyScrollPx;

            if (layoutEligible) {
                reportTableScrollBudget(budgetAllowsFixedLayout);
            }

            const appliedScrollY = enabled && next >= minTableBodyScrollPx ? next : undefined;

            if (import.meta.env.DEV) {
                const anchorSel = heightRootClassName ? buildClassSelector(heightRootClassName) : '';
                const anchor = anchorSel ? region.closest(anchorSel) : null;
                const rr = region.getBoundingClientRect();
                const anchorRect = anchor?.getBoundingClientRect();
                const header = region.querySelector('.ant-table-header');
                const pagination = region.querySelector('.ant-table-pagination');
                const pagRect = pagination?.getBoundingClientRect();
                const cs = getComputedStyle(region);
                const headerH = header?.offsetHeight ?? 0;
                const paginationMeasured = pagination
                    ? (() => {
                          const h = pagination.offsetHeight;
                          const pcs = getComputedStyle(pagination);
                          return h + (Number.parseFloat(pcs.marginTop) || 0) + (Number.parseFloat(pcs.marginBottom) || 0);
                      })()
                    : 0;
                const paginationH = paginationMeasured > 0 ? paginationMeasured : PAGINATION_HEIGHT_FLOOR;
                /** @type {{ selector: string; offsetHeight: number }[]} */
                const siblingDeductions = [];
                if (heightRootClassName) {
                    for (const sel of deductSelectors) {
                        for (const node of region.querySelectorAll(sel)) {
                            siblingDeductions.push({ selector: sel, offsetHeight: node.offsetHeight });
                        }
                    }
                }

                console.log('[useTableScrollY 测量]', {
                    regionClassName: region.className,
                    hasAnchor: !!anchor,
                    anchorBottom: anchorRect?.bottom ?? null,
                    regionHeightPx: Math.round(rr.height * 10) / 10,
                    regionTop: rr.top,
                    regionBottom: rr.bottom,
                    budgetRegionContentPx,
                    probeBudgetPx,
                    siblingDeductions,
                    headerHPx: headerH,
                    paginationMeasuredPx: paginationMeasured,
                    paginationUsedPx: paginationH,
                    scrollYOffsetPx: scrollYOffset,
                    regionPaddingBottom: cs.paddingBottom,
                    regionPaddingTop: cs.paddingTop,
                    gapCardBottomToPaginationBottomPx: anchorRect && pagRect ? Math.round((anchorRect.bottom - pagRect.bottom) * 10) / 10 : null,
                    scrollYRaw: next,
                    probeNextRaw: probeNext,
                    minTableBodyScrollPx,
                    budgetAllowsFixedLayout,
                    appliedScrollY,
                });
            }

            setScrollY(appliedScrollY);
        }

        function syncResizeObservers() {
            for (const ro of resizeObservers) ro.disconnect();
            resizeObservers.length = 0;

            const watch = el => {
                const ro = new ResizeObserver(measure);
                ro.observe(el);
                resizeObservers.push(ro);
            };

            watch(region);
            const wrapper = region.querySelector('.ant-table-wrapper');
            const header = region.querySelector('.ant-table-header');
            const pagination = region.querySelector('.ant-table-pagination');
            if (wrapper) watch(wrapper);
            if (header) watch(header);
            if (pagination) watch(pagination);

            if (heightRootClassName) {
                const sel = buildClassSelector(heightRootClassName);
                if (sel) {
                    const anchor = region.closest(sel);
                    if (anchor) watch(anchor);
                }
            }
        }

        let rafId = 0;
        const scheduleMeasure = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                syncResizeObservers();
                measure();
            });
        };

        let settleRaf = 0;
        const scheduleSettleMeasure = () => {
            cancelAnimationFrame(settleRaf);
            settleRaf = requestAnimationFrame(() => {
                settleRaf = requestAnimationFrame(() => {
                    settleRaf = 0;
                    measure();
                });
            });
        };

        const mutationObserver = new MutationObserver(scheduleMeasure);
        mutationObserver.observe(region, { childList: true, subtree: true });

        const mainEl = region.closest('main.main-container');
        const mainClassObserver =
            mainEl
            && new MutationObserver(() => {
                scheduleMeasure();
                scheduleSettleMeasure();
            });
        if (mainClassObserver && mainEl) {
            mainClassObserver.observe(mainEl, { attributes: true, attributeFilter: ['class'] });
        }

        const onWinResize = () => {
            measure();
        };
        window.addEventListener('resize', onWinResize, { passive: true });
        window.addEventListener(TABLE_SCROLL_Y_REMEASURE_EVENT, onWinResize, { passive: true });

        syncResizeObservers();
        measure();
        scheduleSettleMeasure();

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            cancelAnimationFrame(settleRaf);
            mutationObserver.disconnect();
            mainClassObserver?.disconnect();
            for (const ro of resizeObservers) ro.disconnect();
            window.removeEventListener('resize', onWinResize);
            window.removeEventListener(TABLE_SCROLL_Y_REMEASURE_EVENT, onWinResize);
            if (layoutEligible) {
                reportTableScrollBudget(null);
            }
        };
    }, [tableRegionRef, deductSiblingSelectorsKey, enabled, heightRootClassName, keepAliveActive, layoutEligible, minTableBodyScrollPx, reportTableScrollBudget, scrollYOffset, shouldMeasure]);

    return scrollY;
}
